import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc, getDoc, collection, query, where, onSnapshot, addDoc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, fns, storage } from "../firebase";
import { useSession } from "../auth.jsx";
import { Eyebrow, TierStamp, CheckList } from "../components/Bits.jsx";
import LedgerEditor, { emptyLedger } from "../components/LedgerEditor.jsx";
import { criteriaFor, GATES } from "../lib/rubric.js";

export default function Submit() {
  const { briefId } = useParams();
  const nav = useNavigate();
  const s = useSession();

  const [brief, setBrief] = useState(null);
  const [sub, setSub] = useState(null);
  const [mark, setMark] = useState(null);
  const [urls, setUrls] = useState([""]);
  const [repoUrl, setRepoUrl] = useState("");
  const [readmeUrl, setReadmeUrl] = useState("");
  const [ledger, setLedger] = useState(emptyLedger());
  const [notes, setNotes] = useState("");
  const [tierAttested, setTierAttested] = useState(false);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    getDoc(doc(db, "briefs", briefId)).then((d) => d.exists() && setBrief({ id: d.id, ...d.data() }));
  }, [briefId]);

  useEffect(() => {
    if (!s.user) return;
    return onSnapshot(
      query(collection(db, "submissions"), where("residentUid", "==", s.user.uid), where("briefId", "==", briefId)),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.attempt ?? 1) - (a.attempt ?? 1));
        const latest = rows[0] ?? null;
        setSub(latest);
        if (latest) {
          setUrls(latest.artefactUrls?.length ? latest.artefactUrls : [""]);
          setRepoUrl(latest.repoUrl ?? "");
          setReadmeUrl(latest.readmeUrl ?? "");
          setLedger(latest.ledger ?? emptyLedger());
          setNotes(latest.notes ?? "");
        }
      }
    );
  }, [s.user, briefId]);

  useEffect(() => {
    if (!sub || sub.state !== "released") return setMark(null);
    return onSnapshot(doc(db, "marks", sub.id), (d) => setMark(d.exists() ? d.data() : null));
  }, [sub?.id, sub?.state]);

  const editable = !sub || sub.state === "draft";
  const gateId = useMemo(() => Object.entries(GATES).find(([, g]) => g.brief === briefId)?.[0], [briefId]);

  const payload = () => ({
    artefactUrls: urls.map((u) => u.trim()).filter(Boolean),
    repoUrl: repoUrl.trim() || null,
    readmeUrl: readmeUrl.trim() || null,
    ledger,
    notes,
    tierAttested,
    updatedAt: serverTimestamp(),
  });

  const save = async (state) => {
    setBusy(state); setMsg(null);
    try {
      if (!sub) {
        const created = await addDoc(collection(db, "submissions"), {
          residentUid: s.user.uid,
          briefId,
          tier: brief?.tier ?? null,
          state: "draft",
          attempt: 1,
        });
        await updateDoc(doc(db, "submissions", created.id), { ...payload(), state });
      } else {
        await updateDoc(doc(db, "submissions", sub.id), { ...payload(), state });
      }
      setMsg(state === "submitted"
        ? { kind: "ok", text: "Submitted. Automated checks run first; if they fail you'll get it straight back with the reason." }
        : { kind: "ok", text: "Saved." });
    } catch (e) {
      setMsg({ kind: "error", text: e.message });
    } finally { setBusy(""); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const path = `submissions/${s.user.uid}/${sub?.id ?? "draft"}/${Date.now()}-${file.name}`;
      const r = storageRef(storage, path);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      setUrls([...urls.filter(Boolean), url]);
    } catch (e) {
      setMsg({ kind: "error", text: e.message });
    } finally { setBusy(""); }
  };

  const resubmit = async () => {
    setBusy("resubmit");
    try {
      const call = httpsCallable(fns, "startResubmission");
      const res = await call({ subId: sub.id });
      setMsg({ kind: "ok", text: `New attempt opened (${res.data.subId}). Your previous attempt stays on record.` });
    } catch (e) {
      setMsg({ kind: "error", text: e.message });
    } finally { setBusy(""); }
  };

  if (!brief) return <p style={{ color: "var(--graphite)" }}>Loading brief…</p>;

  const ledgerReady = ledger.signed && (ledger.wrong ?? "").trim().length >= 40
    && ledger.rows.some((r) => r.element && r.human && r.machine && r.verification);
  const canSubmit = editable && ledgerReady && tierAttested && urls.some((u) => u.trim()) && !busy;

  return (
    <div>
      <button className="btn ghost" onClick={() => nav("/studio")} style={{ marginBottom: 14 }}>← All briefs</button>

      <div className="row between wrapflex">
        <div>
          <Eyebrow>{brief.month} · {brief.block}</Eyebrow>
          <h1 style={{ fontSize: 26, marginTop: 4 }}>
            <span className="mono" style={{ fontSize: 17, color: "var(--graphite)", marginRight: 10 }}>{brief.id}</span>
            {brief.title}
          </h1>
        </div>
        <TierStamp tier={brief.tier} />
      </div>

      <div className="grid2" style={{ marginTop: 16 }}>
        <div>
          <div className="card">
            <Eyebrow>The charge</Eyebrow>
            <p style={{ marginTop: 6, fontSize: 17 }}>{brief.charge}</p>
            <Eyebrow style={{ marginTop: 14 }}>Why this exists</Eyebrow>
            <p style={{ marginTop: 6, fontSize: 14 }}>{brief.why}</p>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <Eyebrow>Deliverable</Eyebrow>
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              {(brief.deliverable ?? []).map((d, i) => <li key={i} style={{ marginBottom: 6 }}>{d}</li>)}
            </ul>
          </div>

          <div className="card" style={{ marginTop: 14, borderColor: "var(--madder)" }}>
            <Eyebrow style={{ color: "var(--madder)" }}>AI prohibited on this brief</Eyebrow>
            <p style={{ marginTop: 6 }}>{brief.prohibited}</p>
            <Eyebrow style={{ marginTop: 12, color: "var(--verdigris)" }}>AI permitted</Eyebrow>
            <p style={{ marginTop: 6, fontSize: 14 }}>{brief.permitted}</p>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <Eyebrow>How this is marked</Eyebrow>
            <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 14 }}>
              {criteriaFor(briefId).map((c) => (
                <li key={c.id} style={{ marginBottom: 4 }}>
                  {c.label} <span className="pill">{c.strand}</span>
                </li>
              ))}
            </ul>
            {gateId && (
              <p className="fail" style={{ fontSize: 13, marginTop: 8 }}>
                This brief carries gate {gateId} · {GATES[gateId].name}. Marks cannot compensate for it.
              </p>
            )}
          </div>
        </div>

        {/* ---- the submission ---- */}
        <div>
          {sub?.state === "returned" && (
            <div className="warn" style={{ marginBottom: 14 }}>
              <Eyebrow style={{ color: "var(--madder)" }}>Returned before review</Eyebrow>
              <p style={{ marginTop: 6, fontSize: 14 }}>
                Automated checks failed, so this never reached your Studio Master. Fix these and submit again —
                it does not count as an attempt at the gate.
              </p>
              <div style={{ marginTop: 8 }}><CheckList l1={sub.l1} /></div>
            </div>
          )}

          {mark && (
            <div className="card" style={{ marginBottom: 14, borderColor: "var(--verdigris)" }}>
              <Eyebrow style={{ color: "var(--verdigris)" }}>Marked</Eyebrow>
              <p style={{ marginTop: 8 }}><strong>Keep:</strong> {mark.feedback?.keep}</p>
              <ol style={{ paddingLeft: 18 }}>
                {(mark.feedback?.changes ?? []).map((c, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>
                    {c.change} <span style={{ color: "var(--graphite)" }}>— {c.principle}</span>
                  </li>
                ))}
              </ol>
              <button className="btn ghost" onClick={resubmit} disabled={busy === "resubmit"}>Open a new attempt</button>
            </div>
          )}

          {!editable && !mark && (
            <div className="card" style={{ marginBottom: 14 }}>
              <Eyebrow>Submitted</Eyebrow>
              <p style={{ marginTop: 6, fontSize: 14 }}>
                Your work is frozen while it is with your Studio Master. Feedback is due within 72 hours of it
                reaching the queue.
              </p>
            </div>
          )}

          <div className="card">
            <Eyebrow>Artefacts</Eyebrow>
            {urls.map((u, i) => (
              <input key={i} type="text" placeholder="Link to a file, prototype, repo, or recording"
                value={u} style={{ marginTop: 6 }} disabled={!editable}
                onChange={(e) => setUrls(urls.map((x, j) => (j === i ? e.target.value : x)))} />
            ))}
            {editable && (
              <div className="row" style={{ marginTop: 8, gap: 8 }}>
                <button className="btn ghost" onClick={() => setUrls([...urls, ""])}>+ link</button>
                <label className="btn ghost" style={{ display: "inline-block" }}>
                  {busy === "upload" ? "Uploading…" : "Upload file"}
                  <input type="file" style={{ display: "none" }} onChange={(e) => upload(e.target.files?.[0])} />
                </label>
              </div>
            )}
            {brief.requiresRepo && (
              <>
                <label className="eyebrow" style={{ display: "block", marginTop: 12 }}>Repository (required for this brief)</label>
                <input type="text" value={repoUrl} disabled={!editable} onChange={(e) => setRepoUrl(e.target.value)} />
                <label className="eyebrow" style={{ display: "block", marginTop: 8 }}>README URL</label>
                <input type="text" value={readmeUrl} disabled={!editable} onChange={(e) => setReadmeUrl(e.target.value)} />
              </>
            )}
            <label className="eyebrow" style={{ display: "block", marginTop: 12 }}>Anything your Studio Master should know</label>
            <textarea rows={2} value={notes} disabled={!editable} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <Eyebrow>Provenance ledger</Eyebrow>
            <p style={{ fontSize: 13, color: "var(--graphite)", margin: "4px 0 10px" }}>
              Fill this in as you work. A ledger written afterwards reads like one.
            </p>
            <LedgerEditor value={ledger} onChange={setLedger} readOnly={!editable} />
          </div>

          {editable && (
            <>
              <label className="card" style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "flex-start" }}>
                <input type="checkbox" checked={tierAttested} onChange={(e) => setTierAttested(e.target.checked)} style={{ marginTop: 4 }} />
                <span style={{ fontSize: 14 }}>
                  I worked within Tier {brief.tier} on this brief, and the prohibitions above were respected.
                </span>
              </label>

              {msg && <div className={msg.kind === "ok" ? "card" : "warn"} style={{ marginTop: 14 }}>{msg.text}</div>}

              <div className="row" style={{ marginTop: 14, gap: 10 }}>
                <button className="btn ghost" onClick={() => save("draft")} disabled={busy === "draft"}>Save draft</button>
                <button className="btn" onClick={() => save("submitted")} disabled={!canSubmit}>Submit</button>
              </div>
              {!canSubmit && (
                <p style={{ fontSize: 12, color: "var(--graphite)", marginTop: 8 }}>
                  {!urls.some((u) => u.trim()) && "Attach at least one artefact. "}
                  {!ledger.rows.some((r) => r.element && r.human && r.machine && r.verification) && "Complete at least one ledger row. "}
                  {(ledger.wrong ?? "").trim().length < 40 && "Name one place the machine was wrong. "}
                  {!ledger.signed && "Sign the ledger. "}
                  {!tierAttested && "Attest to the tier."}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
